import { z } from 'zod/v4';

import { getNormalizedFileName } from '../export/exportFileName';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { PcbIdRuntype } from '../idTypes';
import {
    PcbQuotationAddressRuntype,
    PcbQuotationCreationRuntype,
    PcbQuotationListItemRuntype,
    PcbQuotationRuntype,
    QuotationItemCreationRuntype,
    QuotationItemInfoRuntype,
    QuotationItemReferenceRuntype,
    QuotationItemRuntype,
    QuotationStatusRuntype,
} from './pcbQuotationBackendTypes';

/**
 * Endpoints of the Stackrate quotation service (served under /api/quotation): PCB price quotations.
 * Two routes of that service are deliberately absent: printHtml
 * and quotationstream take their token as a `?k=` query parameter, so neither can authorize from the
 * bearer header this client sends.
 */
export const pcbQuotationEndpoints = {
    // ------------------------------------------------------------------
    // Reads
    // ------------------------------------------------------------------

    'GET /quotation/quotations': endpoint({
        description:
            'Paginated list of the team’s PCB quotations (archived/deleted are filtered out). Pages are 1-based, pagesize defaults to 100. With items=true the quotation items are resolved.',
        pathParams: z.undefined(),
        queryParams: z.object({
            items: z.boolean().optional(),
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: z.object({
            results: z.array(PcbQuotationRuntype),
            resultCount: z.number(),
        }),
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    'GET /quotation/quotations/:idOrName': endpoint({
        description: 'A single PCB quotation, by UUID or name. With items=true the quotation items are resolved.',
        pathParams: z.object({ idOrName: z.string() }),
        queryParams: z.object({ items: z.boolean().optional() }),
        requestBody: z.undefined(),
        responseBody: PcbQuotationRuntype,
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    'GET /quotation/search': endpoint({
        description:
            'Paginated PCB quotation search (flat list items, newest first). Filters combine with AND; status is a single exact status name (draft/sent/won/lost/overdue/archived/deleted); origin is api or manual. Pages are 1-based, pagesize defaults to 100.',
        pathParams: z.undefined(),
        queryParams: z.object({
            version: PcbIdRuntype.optional(),
            customer: z.string().optional(),
            contact: z.string().optional(),
            status: z.string().optional(),
            creator: z.string().optional(),
            origin: z.enum(['api', 'manual']).optional(),
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: z.object({
            results: z.array(PcbQuotationListItemRuntype),
            resultCount: z.number(),
        }),
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    'GET /quotation/quotationitems': endpoint({
        description: 'Quotation items of the team, optionally filtered by assembly version and technology.',
        pathParams: z.undefined(),
        queryParams: z.object({
            version: PcbIdRuntype.optional(),
            technology: z.string().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: z.array(QuotationItemRuntype),
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    'GET /quotation/quotationitems/:item': endpoint({
        description: 'A single quotation item by UUID.',
        pathParams: z.object({ item: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: QuotationItemRuntype,
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    'GET /quotation/quotations/all': endpoint({
        description:
            'The PCB quotations of a board, matching `version` against both the assembly version and the share id. Prefer this over GET /quotation/search?version= when the board may be an own assembly: search only filters the shared-reference column, so it silently returns nothing for a plain assembly reference.',
        pathParams: z.undefined(),
        queryParams: z.object({ version: z.string() }),
        requestBody: z.undefined(),
        responseBody: z.array(PcbQuotationRuntype),
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    // ------------------------------------------------------------------
    // Create
    // ------------------------------------------------------------------

    'POST /quotation/quotations': endpoint({
        description: 'Creates a PCB quotation for an assembly and customer.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PcbQuotationCreationRuntype,
        responseBody: PcbQuotationRuntype,
        invalidates: ['GET /quotation/quotations', 'GET /quotation/search'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'POST /quotation/quotationitems': endpoint({
        description: 'Creates a quotation item (position) for an assembly, to be attached to a quotation.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: QuotationItemCreationRuntype,
        responseBody: QuotationItemRuntype,
        invalidates: ['GET /quotation/quotationitems'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'PUT /quotation/quotations/:customer/quotations/:quotation/items': endpoint({
        description: 'Attaches an existing quotation item to a quotation.',
        pathParams: z.object({ customer: z.string(), quotation: z.string() }),
        queryParams: z.undefined(),
        requestBody: QuotationItemReferenceRuntype,
        responseBody: PcbQuotationRuntype,
        invalidates: ['GET /quotation/quotations', 'GET /quotation/quotations/:idOrName'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    // ------------------------------------------------------------------
    // Update
    // ------------------------------------------------------------------

    'PUT /quotation/quotations/:idOrName/status': endpoint({
        description:
            'Moves a PCB quotation to a new status — draft, sent, won, lost, archived or deleted. The date is stamped server-side, so it can be omitted. This is what sends a quotation to the customer.',
        pathParams: z.object({ idOrName: z.string() }),
        queryParams: z.undefined(),
        requestBody: QuotationStatusRuntype,
        responseBody: PcbQuotationRuntype,
        invalidates: [
            'GET /quotation/quotations',
            'GET /quotation/quotations/:idOrName',
            'GET /quotation/quotations/all',
            'GET /quotation/search',
        ],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'PUT /quotation/quotations/:idOrName': endpoint({
        description:
            'Replaces the editable fields of a PCB quotation. Takes the same body as the create call, where `items` are item UUIDs rather than references.',
        pathParams: z.object({ idOrName: z.string() }),
        queryParams: z.undefined(),
        requestBody: PcbQuotationCreationRuntype,
        responseBody: PcbQuotationRuntype,
        invalidates: [
            'GET /quotation/quotations',
            'GET /quotation/quotations/:idOrName',
            'GET /quotation/quotations/all',
            'GET /quotation/search',
        ],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'PUT /quotation/quotations/:quotation/items/:item/status': endpoint({
        description:
            'Marks a quotation item as won or lost. Winning an item also moves the whole quotation to the won status.',
        pathParams: z.object({ quotation: z.string(), item: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.boolean(),
        responseBody: QuotationItemRuntype,
        invalidates: [
            'GET /quotation/quotations',
            'GET /quotation/quotations/:idOrName',
            'GET /quotation/quotations/all',
            'GET /quotation/quotationitems',
            'GET /quotation/quotationitems/:item',
            'GET /quotation/search',
        ],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'PUT /quotation/quotationitems/:item': endpoint({
        description:
            'Replaces the info of a quotation item — quantities, prices, delivery and the user overrides — and recomputes its totals.',
        pathParams: z.object({ item: z.string() }),
        queryParams: z.undefined(),
        requestBody: QuotationItemInfoRuntype,
        responseBody: QuotationItemRuntype,
        invalidates: [
            'GET /quotation/quotationitems',
            'GET /quotation/quotationitems/:item',
            'GET /quotation/quotations',
            'GET /quotation/quotations/:idOrName',
        ],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'PUT /quotation/quotations/:customer/quotations/:quotation/addresses/billing': endpoint({
        description: 'Sets the billing address of a quotation.',
        pathParams: z.object({ customer: z.string(), quotation: z.string() }),
        queryParams: z.undefined(),
        requestBody: PcbQuotationAddressRuntype,
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return null;
            }
            throw Error(`Failed to set the billing address: ${response.status}`);
        },
        invalidates: ['GET /quotation/quotations', 'GET /quotation/quotations/:idOrName'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'PUT /quotation/quotations/:customer/quotations/:quotation/addresses/shipping': endpoint({
        description: 'Sets the shipping address of a quotation.',
        pathParams: z.object({ customer: z.string(), quotation: z.string() }),
        queryParams: z.undefined(),
        requestBody: PcbQuotationAddressRuntype,
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return null;
            }
            throw Error(`Failed to set the shipping address: ${response.status}`);
        },
        invalidates: ['GET /quotation/quotations', 'GET /quotation/quotations/:idOrName'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    // ------------------------------------------------------------------
    // Delete
    // ------------------------------------------------------------------

    'DELETE /quotation/quotations/quotations/:idOrName': endpoint({
        description:
            'Soft-deletes a PCB quotation, which drops it out of the list and search results. The doubled path segment is what the service declares.',
        pathParams: z.object({ idOrName: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return null;
            }
            throw Error(`Failed to delete the quotation: ${response.status}`);
        },
        invalidates: ['GET /quotation/quotations', 'GET /quotation/quotations/all', 'GET /quotation/search'],
        removes: ['GET /quotation/quotations/:idOrName'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'DELETE /quotation/quotationitems/:item': endpoint({
        description: 'Deletes a quotation item. It is not detached from the quotations referencing it.',
        pathParams: z.object({ item: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return null;
            }
            throw Error(`Failed to delete the quotation item: ${response.status}`);
        },
        invalidates: ['GET /quotation/quotationitems'],
        removes: ['GET /quotation/quotationitems/:item'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    'DELETE /quotation/quotations/:customer/quotations/:quotation/items/:item': endpoint({
        description:
            'Detaches a quotation item from a quotation, leaving the item itself in place. Only useful while the quotation is still a draft, the one status that allows item changes.',
        pathParams: z.object({ customer: z.string(), quotation: z.string(), item: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PcbQuotationRuntype,
        invalidates: ['GET /quotation/quotations', 'GET /quotation/quotations/:idOrName'],
        actionPermission: () => 'CanEditPcbQuotation',
    }),

    // ------------------------------------------------------------------
    // Documents
    // ------------------------------------------------------------------

    'GET /quotation/quotations/:idOrName/print': endpoint({
        description: 'Renders the customer-facing quotation document as a PDF.',
        pathParams: z.object({ idOrName: z.string() }),
        queryParams: z.object({ template: z.string().optional() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
        actionPermission: () => 'CanViewPcbQuotation',
    }),

    'GET /quotation/quotations/export/csv': endpoint({
        description:
            'Exports every quotation of the team as a zip of CSV files. Luminovo staff only — the route is gated on a staff email rather than on an action permission, so it has no actionPermission to check against.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
    }),
};
